/**
 * The automation's trigger: one select (Manual / Scheduled / Webhook / On new
 * row / Row changed) with the setting that belongs to the chosen trigger
 * underneath — the schedule preset for Scheduled, the webhook (and "store in
 * table") for Webhook, the watched columns for Row changed. Both row triggers
 * say that the input filter decides which rows run and lead there. Shared by the sidebar start page and the settings panel's
 * trigger tile so both always edit the same thing the same way.
 */
import { Button } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { SignalIcon, TableIcon } from '@blocksuite/icons/rc';
import {
  isProcessCronPreset,
  PROCESS_CRON_PRESET_VALUES,
  type ProcessCronPreset,
} from '@streamlain/automation-safety-contract';
import type { FC } from 'react';
import { useCallback } from 'react';

import type { InboundWebhook } from '../hooks/use-inbound-webhooks';
import type { AutomationConfig } from '../services/automation';
import { DEFAULT_STATUS_TRACKING_CONFIG } from '../services/automation';
import type { ColumnInfo } from '../services/database-discovery';
import type { TriggerType } from '../types';
import {
  DEFAULT_TRIGGER_CONFIG,
  isActivatableTrigger,
  isRowTrigger,
  trackedStatusColumnId,
} from '../types';
import * as settingStyles from './automation-settings-panel.css';
import { PlanUsageSection } from './components/plan-usage-section';
import { SectionHint } from './components/section-layout';
import { SettingSelect } from './components/setting-select';
import { WatchedColumnsField } from './components/watched-columns-field';
import { TRIGGER_ICONS } from './trigger-icons';
import * as styles from './trigger-settings.css';

type SchedulePreset = ProcessCronPreset;

const CRON_LABEL_KEYS: Record<SchedulePreset, string> = {
  every_1_min: 'com.streamlain.streamlain.process.cron.every-1-min',
  every_5_min: 'com.streamlain.streamlain.process.cron.every-5-min',
  every_15_min: 'com.streamlain.streamlain.process.cron.every-15-min',
  every_30_min: 'com.streamlain.streamlain.process.cron.every-30-min',
  hourly: 'com.streamlain.streamlain.process.cron.hourly',
  daily_9am: 'com.streamlain.streamlain.process.cron.daily-9am',
  daily_6pm: 'com.streamlain.streamlain.process.cron.daily-6pm',
  weekly_monday_9am: 'com.streamlain.streamlain.process.cron.weekly-monday',
  monthly_first_9am: 'com.streamlain.streamlain.process.cron.monthly-first',
};

export interface TriggerSettingsProps {
  config: AutomationConfig;
  onUpdate: (updates: Partial<AutomationConfig>) => void;
  /** Persists the backend schedule state; resolves false when it failed. */
  onScheduleToggle: (enabled: boolean) => Promise<boolean>;
  isScheduleSaving: boolean;
  canEdit: boolean;
  /** Webhook triggers are a workflow-plan feature. */
  webhookAllowed: boolean;
  webhooks: readonly InboundWebhook[];
  /** Creates an incoming table for webhook calls and sets it as the output. */
  onStoreWebhookInTable: () => void;
  /** Whether the selected workflow has input fields to build that table from. */
  canStoreWebhookInTable: boolean;
  workspaceId: string;
  /** The automation block; its use of the plan limits shows below. */
  blockId: string | null;
  /** The source table's columns, for the columns "Row changed" watches. */
  sourceColumns: readonly ColumnInfo[];
  /** Opens the input filter, which decides the rows a row trigger runs. */
  onOpenFilter: () => void;
}

export const TriggerSettings: FC<TriggerSettingsProps> = ({
  config,
  onUpdate,
  onScheduleToggle,
  isScheduleSaving,
  canEdit,
  webhookAllowed,
  webhooks,
  onStoreWebhookInTable,
  canStoreWebhookInTable,
  workspaceId,
  blockId,
  sourceColumns,
  onOpenFilter,
}) => {
  const t = useI18n();
  const triggerConfig = config.triggerConfig ?? DEFAULT_TRIGGER_CONFIG;
  const type = triggerConfig.type;
  const disabled = !canEdit || isScheduleSaving;

  /** An active schedule is switched off on the backend before it changes. */
  const pauseIfActive = useCallback(async () => {
    if (!triggerConfig.enabled || !isActivatableTrigger(triggerConfig.type)) {
      return true;
    }
    return onScheduleToggle(false);
  }, [onScheduleToggle, triggerConfig.enabled, triggerConfig.type]);

  const handleTypeChange = useCallback(
    async (next: TriggerType) => {
      if (next === type || !(await pauseIfActive())) return;
      const updates: Partial<AutomationConfig> = {
        triggerConfig: {
          ...triggerConfig,
          type: next,
          enabled: false,
          webhookCredentialId:
            next === 'webhook'
              ? (triggerConfig.webhookCredentialId ?? null)
              : null,
        },
      };
      if (next === 'webhook' || type === 'webhook') {
        // The webhook is the input: tables, their filters and column
        // mappings belong to the other triggers and do not carry over.
        Object.assign(updates, {
          sourceTableId: null,
          sourceTableName: null,
          sourceDocId: null,
          selectedColumns: [],
          filterGroup: undefined,
          inputTables: [],
          inputMappings: [],
          statusTracking: {
            ...(config.statusTracking ?? DEFAULT_STATUS_TRACKING_CONFIG),
            enabled: false,
            columnId: null,
          },
        });
      }
      onUpdate(updates);
    },
    [config.statusTracking, onUpdate, pauseIfActive, triggerConfig, type]
  );

  const handlePresetChange = useCallback(
    async (cronPreset: SchedulePreset) => {
      if (!(await pauseIfActive())) return;
      onUpdate({
        triggerConfig: { ...triggerConfig, cronPreset, enabled: false },
      });
    },
    [onUpdate, pauseIfActive, triggerConfig]
  );

  const handleWatchedColumnsChange = useCallback(
    async (watchedColumnIds: string[]) => {
      if (!(await pauseIfActive())) return;
      onUpdate({
        triggerConfig: { ...triggerConfig, watchedColumnIds, enabled: false },
      });
    },
    [onUpdate, pauseIfActive, triggerConfig]
  );

  const handleWebhookChange = useCallback(
    async (webhookCredentialId: string) => {
      if (!(await pauseIfActive())) return;
      onUpdate({
        triggerConfig: {
          ...triggerConfig,
          webhookCredentialId,
          enabled: false,
        },
      });
    },
    [onUpdate, pauseIfActive, triggerConfig]
  );

  const triggerLabel = (value: TriggerType) =>
    t.t(`com.streamlain.automation.sidebar.trigger.${value}`);
  const types: TriggerType[] = [
    'manual',
    'scheduled',
    ...(webhookAllowed || type === 'webhook' ? (['webhook'] as const) : []),
    'row_created',
    'row_changed',
  ];
  const preset: SchedulePreset =
    triggerConfig.cronPreset && isProcessCronPreset(triggerConfig.cronPreset)
      ? triggerConfig.cronPreset
      : 'hourly';
  const webhook = webhooks.find(
    entry => entry.credentialId === triggerConfig.webhookCredentialId
  );

  return (
    <>
      <div data-coachmark-target="trigger-type-menu">
        <SettingSelect
          value={type}
          options={types.map(value => ({
            value,
            label: triggerLabel(value),
            icon: TRIGGER_ICONS[value],
          }))}
          onChange={value => void handleTypeChange(value)}
          disabled={disabled}
          ariaLabel={t['com.streamlain.automation.sidebar.trigger-label']()}
          data-testid="trigger-type-select"
        />
      </div>
      {type === 'scheduled' && (
        <SettingSelect
          value={preset}
          options={PROCESS_CRON_PRESET_VALUES.map(key => ({
            value: key,
            label: t.t(CRON_LABEL_KEYS[key]),
          }))}
          onChange={value => void handlePresetChange(value)}
          disabled={disabled}
          ariaLabel={t['com.streamlain.streamlain.process.schedule']()}
          data-testid="trigger-schedule-select"
        />
      )}
      {type === 'webhook' && (
        <>
          <SettingSelect
            value={webhook?.credentialId ?? null}
            options={webhooks.map(entry => ({
              value: entry.credentialId,
              label: entry.name,
              icon: <SignalIcon />,
            }))}
            onChange={webhookCredentialId =>
              void handleWebhookChange(webhookCredentialId)
            }
            placeholder={t[
              'com.streamlain.automation.sidebar.webhook-select'
            ]()}
            disabled={disabled || webhooks.length === 0}
            ariaLabel={t['com.streamlain.automation.sidebar.webhook-select']()}
            data-testid="trigger-webhook-select"
          />
          <Button
            block
            variant="secondary"
            prefix={<TableIcon />}
            disabled={disabled || !canStoreWebhookInTable}
            title={
              canStoreWebhookInTable
                ? t[
                    'com.streamlain.automation.sidebar.webhook-store-in-table-hint'
                  ]()
                : t[
                    'com.streamlain.automation.sidebar.webhook-table-needs-schema'
                  ]()
            }
            onClick={onStoreWebhookInTable}
          >
            {t['com.streamlain.automation.sidebar.webhook-store-in-table']()}
          </Button>
        </>
      )}
      {type === 'row_changed' && (
        <WatchedColumnsField
          columns={sourceColumns}
          value={triggerConfig.watchedColumnIds ?? []}
          statusColumnId={trackedStatusColumnId(config.statusTracking)}
          onChange={columns => void handleWatchedColumnsChange(columns)}
          disabled={disabled}
        />
      )}
      <span className={settingStyles.settingHint}>
        {t.t(`com.streamlain.automation.sidebar.trigger.${type}-hint`)}
      </span>
      {isRowTrigger(type) && (
        <SectionHint>
          {type === 'row_created' && (
            <span className={styles.rowRule}>
              {t[
                'com.streamlain.automation.sidebar.trigger.row_created-after-switch-on'
              ]()}
            </span>
          )}
          <span className={styles.rowRule}>
            {t[
              'com.streamlain.automation.sidebar.trigger.rows-follow-filter'
            ]()}
            <button
              type="button"
              className={styles.filterLink}
              onClick={onOpenFilter}
              data-testid="trigger-open-filter"
            >
              {t['com.streamlain.automation.sidebar.trigger.open-filter']()}
            </button>
          </span>
        </SectionHint>
      )}
      {blockId && (
        <PlanUsageSection
          workspaceId={workspaceId}
          blockId={blockId}
          refreshKey={`${type}:${triggerConfig.webhookCredentialId ?? ''}:${triggerConfig.enabled}`}
        />
      )}
    </>
  );
};
